import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, includes, isArray } from 'lodash';
import { compose, withProps, withHandlers } from 'recompose';
import { Expand } from '@sparefoot/react-core';
import { withShowHide } from '@sparefoot/react-core/decorators';

import { Button } from 'components/core/Button';
import { Checkbox } from 'components/core/Checkbox';
import { RadioGroup, RadioInput } from 'components/core/RadioInput';
import Caret from 'components/svg/Caret';

import './FilterGroup.scss';

export const enhance = compose(
	withShowHide('filters', (props) => !!props.showing),
	withHandlers({
		handleOnChange: ({ allowMultiple, onFilter, searchParams, delayFiltering }) => ({ target }) => {
			const { checked, value, name } = target;
			if (allowMultiple) {
				const params = get(searchParams, name, []);
				onFilter({ [name]: checked ?
					[...params, value] :
					params.filter((param) => param !== value) }, delayFiltering);
			} else {
				onFilter({ [name]: checked ? value : null }, delayFiltering);
			}
		},
		handleShowModal: ({ modal, onShowModal }) => () => onShowModal(modal.component)
	}),
	withProps(({ options, filterName, filtersShowing }) => ({
		segmentLabel: `${filterName}-filter-${filtersShowing ? 'collapse' : 'expand'}`,
		defaultRadioValue: get(options.find((option) => option.default), 'value')
	}))
);

/**
 * FilterGroup is a collapsable group of radio or checkbox inputs.
 */
export function FilterGroup({
	defaultRadioValue, label, filterName, filtersShowing, filterType,
	segmentLabel, onToggleFilters, searchParams, handleOnChange, options,
	modal, handleShowModal, listKey
}) {
	return (
		<div
			className={
				classnames(
					'filter-group',
					{ expanded: filtersShowing }
				)
			}
		>
			<Button
				onClick={onToggleFilters}
				segmentLabel={segmentLabel}
				className="filter-group-label"
			>
				<Caret /> {label}
			</Button>
			<Expand showing={filtersShowing}>
				<div>
					<div className="filter-options">
						{filterType === 'radio' &&
							<RadioGroup
								value={searchParams[filterName] || defaultRadioValue}
								onChange={handleOnChange}
								segmentLabel={`${filterName}-filter`}
							>
								{options.map((option) => (
									<RadioInput
										key={option.value}
										id={`${listKey}-${option.name}-${option.value}`}
										name={option.name}
										label={option.label}
										value={option.value}
										segmentLabel={`${option.name}-filter-${option.value}`}
									/>
								))}
							</RadioGroup>
						}
						{filterType !== 'radio' &&
							options.map((option) => (
								<Checkbox
									key={option.value}
									id={`${listKey}-${option.name}-${option.value}`}
									label={`${option.label}`}
									name={option.name}
									value={option.value}
									onChange={handleOnChange}
									segmentLabel={`${option.name}-filter-${option.value}`}
									checked={
										searchParams[option.name] === `${option.value}` ||
										(isArray(searchParams[option.name]) && includes(searchParams[option.name], `${option.value}`))
									}
								/>
							))
						}
					</div>
					{!!modal &&
						<div className="filter-modal">
							<Button
								link
								onClick={handleShowModal}
								segmentLabel={modal.title}
								className="modal-button"
							>
								{modal.title}
							</Button>
						</div>
					}
				</div>
			</Expand>
		</div>
	);
}

FilterGroup.propTypes = {
	listKey: PropTypes.string.isRequired,
	defaultRadioValue: PropTypes.string,
	filterName: PropTypes.string.isRequired,
	filtersShowing: PropTypes.bool.isRequired,
	filterType: PropTypes.oneOf(['radio', 'checkbox']),
	handleOnChange: PropTypes.func.isRequired,
	label: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			facetName: PropTypes.string
		}),
	),
	modal: PropTypes.shape({
		title: PropTypes.string,
		component: PropTypes.func.isRequired
	}),
	handleShowModal: PropTypes.func.isRequired,
	onToggleFilters: PropTypes.func.isRequired,
	searchParams: PropTypes.object,
	segmentLabel: PropTypes.string.isRequired
};

export default enhance(FilterGroup);
